import { useState, useCallback, useRef, type ReactNode } from "react";
import { useQuery, useApolloClient } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { useChatStream } from "@/hooks/useChatStream";
import { GetConversationsDocument } from "@/graphql/getConversations.api";
import { GetPortfoliosDocument } from "@/graphql/getPortfolios.api";
import type { Portfolio } from "@/graphql/types";
import { NEW_PORTFOLIO_KEY } from "@/constants/storage";
import { DashboardChatContext } from "@/contexts/dashboardChatDefs";

export function DashboardChatProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";
  const apolloClient = useApolloClient();
  const [historyOpen, setHistoryOpen] = useState(false);
  const portfolioSlugsBeforeRef = useRef<Set<string>>(new Set());

  const { data: conversationsData, refetch: refetchConversations } = useQuery(
    GetConversationsDocument,
    { skip: !isDashboard }
  );

  const capturePortfoliosBefore = useCallback(() => {
    const cached = apolloClient.readQuery({ query: GetPortfoliosDocument });
    const slugs = cached?.portfolios?.map((p: Pick<Portfolio, "slug">) => p.slug) || [];
    portfolioSlugsBeforeRef.current = new Set(slugs);
  }, [apolloClient]);

  const refetchPortfoliosAndHighlight = useCallback(async () => {
    const result = await apolloClient.refetchQueries({ include: [GetPortfoliosDocument] });
    const refetchResult = result.find((r: { data?: { portfolios?: Portfolio[] } }) =>
      r.data?.portfolios
    );
    const newPortfolios = refetchResult?.data?.portfolios || [];
    for (const p of newPortfolios) {
      if (!portfolioSlugsBeforeRef.current.has(p.slug)) {
        localStorage.setItem(NEW_PORTFOLIO_KEY, p.slug);
        break;
      }
    }
  }, [apolloClient]);

  const {
    messages,
    isStreaming,
    conversationId,
    pendingImport,
    sendMessage: originalSendMessage,
    stopStreaming,
    loadConversation,
    startNewChat: rawStartNewChat,
    clearPendingImport,
  } = useChatStream({
    onConversationCreated: () => refetchConversations(),
    onStreamComplete: refetchPortfoliosAndHighlight,
  });

  const sendMessage = useCallback(
    async (content: string, file?: File) => {
      capturePortfoliosBefore();
      return originalSendMessage(content, file);
    },
    [capturePortfoliosBefore, originalSendMessage]
  );

  const handleNewChat = useCallback(() => {
    rawStartNewChat();
    setHistoryOpen(false);
  }, [rawStartNewChat]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      loadConversation(id, []);
      setHistoryOpen(false);
    },
    [loadConversation]
  );

  const handleDeleteConversation = useCallback(
    (id: string) => {
      refetchConversations();
      if (conversationId === id) {
        rawStartNewChat();
      }
    },
    [refetchConversations, conversationId, rawStartNewChat]
  );

  const handleRenameConversation = useCallback(() => {
    refetchConversations();
  }, [refetchConversations]);

  const conversations = conversationsData?.conversations || [];

  return (
    <DashboardChatContext.Provider
      value={{
        messages,
        isStreaming,
        conversationId,
        pendingImport,
        sendMessage,
        stopStreaming,
        loadConversation,
        startNewChat: rawStartNewChat,
        clearPendingImport,
        historyOpen,
        setHistoryOpen,
        conversations,
        handleNewChat,
        handleSelectConversation,
        handleDeleteConversation,
        handleRenameConversation,
      }}
    >
      {children}
    </DashboardChatContext.Provider>
  );
}
